"use client";

import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { useGSAP } from "@gsap/react";

import lightGreyLeft from "@/app/assets/waves/light-grey-left.svg";
import lightGreyRight from "@/app/assets/waves/light-grey-right.svg";
import darkGreyLeft from "@/app/assets/waves/dark-grey-left.svg";
import darkGreyRight from "@/app/assets/waves/dark-grey-right.svg";
import darkPurpleLeft from "@/app/assets/waves/dark-purple-left.svg";
import darkPurpleRight from "@/app/assets/waves/dark-purple-right.svg";
import lightPurpleToDarkPurpleLeft from "@/app/assets/waves/light-purple-to-dark-purple-left.svg";
import lightPurpleToDarkPurpleRight from "@/app/assets/waves/light-purple-to-dark-purple-right.svg";
import darkPurpleToLightPurpleLeft from "@/app/assets/waves/dark-purple-to-light-purple-left.svg";
import darkPurpleToLightPurpleRight from "@/app/assets/waves/dark-purple-to-light-purple-right.svg";
import lightPurpleToLightBlueLeft from "@/app/assets/waves/light-purple-to-light-blue-left.svg";
import lightPurpleToLightBlueRight from "@/app/assets/waves/light-purple-to-light-blue-right.svg";
import rainbowPurpleToBlueLeft from "@/app/assets/waves/rainbow-purple-to-blue-left.svg";
import rainbowPurpleToBlueRight from "@/app/assets/waves/rainbow-purple-to-blue-right.svg";
import rainbowBlueToPurpleLeft from "@/app/assets/waves/rainbow-blue-to-purple-left.svg";
import rainbowBlueToPurpleRight from "@/app/assets/waves/rainbow-blue-to-purple-right.svg";
import lightPurpleLeft from "@/app/assets/waves/light-purple-left.svg";
import lightPurpleRight from "@/app/assets/waves/light-purple-right.svg";
import midPurpleLeft from "@/app/assets/waves/mid-purple-left.svg";
import midPurpleRight from "@/app/assets/waves/mid-purple-right.svg";
import purpleLeft from "@/app/assets/waves/purple-left.svg";
import purpleRight from "@/app/assets/waves/purple-right.svg";
import darkPurpleInvertedRight from "@/app/assets/waves/dark-purple-right-inverted.svg";

gsap.registerPlugin(ScrollTrigger);

type CurveProps = {
    color: string;
    direction?: string;
    className?: string;
};

export default function BottomCurves({
    color,
    direction,
    className,
}: CurveProps) {
    const curveRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    let imageSrc = null;

    if (direction === "left") {
        switch (true) {
            case color === "light grey":
                imageSrc = lightGreyLeft;
                break;
            case color === "dark grey":
                imageSrc = darkGreyLeft;
                break;
            case color === "dark purple":
                imageSrc = darkPurpleLeft;
                break;
            case color === "light purple to dark purple":
                imageSrc = lightPurpleToDarkPurpleLeft;
                break;
            case color === "dark purple to light purple":
                imageSrc = darkPurpleToLightPurpleLeft;
                break;
            case color === "light purple to light blue":
                imageSrc = lightPurpleToLightBlueLeft;
                break;
            case color === "rainbow blue to purple":
                imageSrc = rainbowBlueToPurpleLeft;
                break;
            case color === "rainbow purple to blue":
                imageSrc = rainbowPurpleToBlueLeft;
                break;
            case color === "light purple":
                imageSrc = lightPurpleLeft;
                break;
            case color === "purple":
                imageSrc = purpleLeft;
                break;
            case color === "mid purple":
                imageSrc = midPurpleLeft;
                break;
            default:
                break;
        }
    }

    if (direction === "right") {
        switch (true) {
            case color === "light grey":
                imageSrc = lightGreyRight;
                break;
            case color === "dark grey":
                imageSrc = darkGreyRight;
                break;
            case color === "dark purple":
                imageSrc = darkPurpleRight;
                break;
            case color === "light purple to dark purple":
                imageSrc = lightPurpleToDarkPurpleRight;
                break;
            case color === "dark purple to light purple":
                imageSrc = darkPurpleToLightPurpleRight;
                break;
            case color === "light purple to light blue":
                imageSrc = lightPurpleToLightBlueRight;
                break;
            case color === "rainbow blue to purple":
                imageSrc = rainbowBlueToPurpleRight;
                break;
            case color === "rainbow purple to blue":
                imageSrc = rainbowPurpleToBlueRight;
                break;
            case color === "light purple":
                imageSrc = lightPurpleRight;
                break;
            case color === "purple":
                imageSrc = purpleRight;
                break;
            case color === "mid purple":
                imageSrc = midPurpleRight;
                break;
            case color === "dark purple inverted":
                imageSrc = darkPurpleInvertedRight;
                break;
            default:
                break;
        }
    }

    useGSAP(() => {
        if (!isMounted) return;

        const ctx = gsap.context(() => {
            gsap.timeline({
                scrollTrigger: {
                    trigger: curveRef.current,
                    start: "top 85%",
                    end: "bottom top",
                    toggleActions: "play reverse play reverse",
                    scrub: 1,
                },
            }).to(curveRef.current, {
                scaleY: 0.25,
                transformOrigin: "bottom",
            });
        }, curveRef);

        return () => ctx.revert();
    }, [isMounted]);

    return (
        <div
            className={`absolute bottom-0 left-0 right-0 m-auto -mb-[1px] w-[105%] -translate-x-[2.5%] pointer-events-none z-0 flex h-1/2 items-end ${className} ${
                direction === "left" ? "flip" : ""
            }`}
        >
            {imageSrc && (
                <Image
                    src={imageSrc}
                    width={1920}
                    height={260}
                    alt="background image"
                    className={`w-full bg-top object-cover`}
                    ref={curveRef}
                />
            )}
        </div>
    );
}
